import { Directive, ElementRef, Input, OnDestroy } from '@angular/core'
import { ReplaySubject, combineLatest } from 'rxjs'
import { isThisYear, isSameMonth, isSameDay, isSameYear } from 'date-fns'
import { map, takeUntil } from 'rxjs/operators'
import { legacyParse } from '@date-fns/upgrade/v2'
import { safeDateString } from '../../../shared/utility-functions/date.utility'

export type DisplayDateVariant = number

@Directive({
  selector: 'time[floDisplayDate]'
})
export class DisplayDateDirective implements OnDestroy {
  // TODO: Utilize src/app/shared/utility-functions/date.utility.ts when this directive is finalized.
  private readonly ngOnDestroy$ = new ReplaySubject<void>(1)
  private readonly nativeElement$ = new ReplaySubject<HTMLTimeElement>(1)
  private readonly date$ = new ReplaySubject<string>(1)
  private readonly endDate$ = new ReplaySubject<string | undefined>(1)
  private readonly variant$ = new ReplaySubject<DisplayDateVariant>(1)
  private readonly tbd$ = new ReplaySubject<boolean | undefined>(1)

  constructor(elementRef: ElementRef) {
    this.nativeElement$.next(elementRef.nativeElement)
    this.variant$.next(0)
  }

  @Input()
  set date(date: Date | string | number) {
    if (date !== undefined) {
      this.date$.next(safeDateString(date))
    }
  }

  @Input()
  set endDate(date: Date | string | number) {
    if (!date) {
      this.endDate$.next(undefined)
    } else {
      this.endDate$.next(safeDateString(date))
    }
  }

  @Input()
  set variant(variant: DisplayDateVariant) {
    this.variant$.next(variant * 1)
  }

  @Input() set isTbd(isTbd: boolean | undefined) {
    isTbd ? this.tbd$.next(isTbd) : this.tbd$.next(false)
  }

  private readonly textContent$ = combineLatest([this.date$, this.endDate$, this.variant$, this.tbd$]).pipe(
    map(([date, endDate, variant, isTbd]) =>
      endDate
        ? this.getDateRangeDisplayText(date, endDate, variant, isTbd)
        : this.getDateDisplayText(date, variant, isTbd)
    )
  )

  public readonly updateNativeElement = combineLatest([this.textContent$, this.date$, this.nativeElement$])
    .pipe(takeUntil(this.ngOnDestroy$))
    .subscribe(([textContent, date, nativeElement]) => {
      nativeElement.textContent = textContent
      nativeElement.dateTime = date
    })

  public ngOnDestroy() {
    this.ngOnDestroy$.next()
    this.ngOnDestroy$.complete()
  }

  getDateDisplayText = (date: string, variant: DisplayDateVariant, isTbd: boolean | undefined): string => {
    switch (variant) {
      case 3:
        return isThisYear(legacyParse(date))
          ? this.setDateFormat(date, isTbd, 'MMM d', 'h:mm a, MMM d')
          : this.setDateFormat(date, isTbd, 'MMM d, yyyy', 'h:mm a, MMM d, yyyy')
      case 2:
        return isThisYear(legacyParse(date)) ? safeDateString(date, 'MMM d') : safeDateString(date, 'MMM d, yyyy')
      case 1:
        return isTbd ? `${safeDateString(date, 'yyyy-MM-dd')} TBD` : safeDateString(date, 'yyyy-MM-dd HH:mm:ss')
      case 0:
      default:
        return date.toString()
    }
  }

  getDateRangeDisplayText = (
    startDate: string,
    endDate: string,
    variant: DisplayDateVariant,
    isTbd: boolean | undefined
  ): string => {
    switch (variant) {
      case 3:
        if (isSameDay(legacyParse(startDate), legacyParse(endDate))) {
          return this.getDateDisplayText(startDate, variant, isTbd)
        } else if (isSameMonth(legacyParse(startDate), legacyParse(endDate))) {
          const startText = this.setDateFormat(startDate, isTbd, 'MMM d', 'h:mm a, MMM d')
          const endText = isThisYear(legacyParse(endDate))
            ? safeDateString(endDate, 'd')
            : safeDateString(endDate, 'd, yyyy')
          return `${startText} - ${endText}`
        } else if (isSameYear(legacyParse(startDate), legacyParse(endDate))) {
          const startText = this.setDateFormat(startDate, isTbd, 'MMM d', 'h:mm a, MMM d')
          const endText = isThisYear(legacyParse(endDate))
            ? safeDateString(endDate, 'MMM d')
            : safeDateString(endDate, 'MMM d, yyyy')
          return `${startText} - ${endText}`
        } else {
          const startText = this.getDateDisplayText(startDate, variant, isTbd)
          const endText = this.getDateDisplayText(endDate, variant, isTbd)
          return `${startText} - ${endText}`
        }
      case 2:
        if (isSameDay(legacyParse(startDate), legacyParse(endDate))) {
          return this.getDateDisplayText(startDate, variant, isTbd)
        } else if (isSameMonth(legacyParse(startDate), legacyParse(endDate))) {
          const startText = safeDateString(startDate, 'MMM d')
          const endText = isThisYear(legacyParse(endDate))
            ? safeDateString(endDate, 'd')
            : safeDateString(endDate, 'd, yyyy')
          return `${startText} - ${endText}`
        } else if (isSameYear(legacyParse(startDate), legacyParse(endDate))) {
          const startText = safeDateString(startDate, 'MMM d')
          const endText = isThisYear(legacyParse(endDate))
            ? safeDateString(endDate, 'MMM d')
            : safeDateString(endDate, 'MMM d, yyyy')
          return `${startText} - ${endText}`
        } else {
          const startText = this.getDateDisplayText(startDate, variant, isTbd)
          const endText = this.getDateDisplayText(endDate, variant, isTbd)
          return `${startText} - ${endText}`
        }
      case 1:
      case 0:
      default:
        return `${this.getDateDisplayText(startDate, variant, isTbd)} - ${this.getDateDisplayText(
          endDate,
          variant,
          isTbd
        )}`
    }
  }

  setDateFormat(date: string, isTbd: boolean | undefined, formatOne: string, formatTwo: string) {
    return isTbd ? `TBD, ${safeDateString(date, `${formatOne}`)}` : safeDateString(date, `${formatTwo}`)
  }
}
